import {
  formatUSPhoneForDisplay,
  normalizePhoneNumber,
  UNKNOWN_CONTACT_NAME,
} from '@super/shared-types'
import type { ChatThread } from '../types'
import type { ThreadWithDetails } from '../../../lib/api/threads.client'

/**
 * Check if a contact name is the unknown placeholder
 * @param name - Contact name to check
 * @returns true if the contact is unknown (null, undefined, or "Unknown Contact")
 */
export function isUnknownContact(name?: string | null): boolean {
  return !name || name === UNKNOWN_CONTACT_NAME
}

/**
 * Get display information for contact header (title and subtitle)
 * For unknown contacts, shows phone/email as title with no subtitle
 * For known contacts, shows name as title with phone/email as subtitle
 *
 * @param thread - Thread object with type and participant info
 * @param threadDetails - Detailed thread data with contact info
 * @returns Object with title and subtitle (subtitle is null for unknown contacts)
 */
export function getContactDisplayInfo(
  thread: ChatThread,
  threadDetails?: ThreadWithDetails | null,
): { title: string; subtitle: string | null } {
  // Get contact info (phone or email)
  const contactInfo =
    thread.threadType === 'PHONE'
      ? (() => {
          const phoneNumber =
            threadDetails?.phone || thread.participant.phone || ''
          if (!phoneNumber) return 'No phone number'
          // Normalize phone from OpenAI's 10-digit format to E.164 before formatting
          const normalizedPhone =
            normalizePhoneNumber(phoneNumber) || phoneNumber
          return formatUSPhoneForDisplay(normalizedPhone) || 'No phone number'
        })()
      : threadDetails?.email || thread.participant.email || 'No email'

  const isUnknown = isUnknownContact(threadDetails?.contact?.name)

  return {
    title: isUnknown
      ? contactInfo
      : threadDetails?.contact?.name || contactInfo,
    subtitle: isUnknown ? null : contactInfo,
  }
}
